import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import './ServiceDetailPage.css';
import { getServiceById } from './servicesData';

const ServiceDetailPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const serviceId = params.get('serviceId');
  const service = getServiceById(serviceId);

  // 预约弹窗状态
  const [showBookingModal, setShowBookingModal] = useState(false);

  // 收藏功能状态
  const [favorites, setFavorites] = useState([]);
  const [isFavorited, setIsFavorited] = useState(false);

  // 加载收藏状态
  useEffect(() => {
    const storedFavorites = localStorage.getItem('housekeeping_favorites');
    if (storedFavorites) {
      try {
        const favoritesArray = JSON.parse(storedFavorites);
        setFavorites(favoritesArray);
        setIsFavorited(favoritesArray.includes(parseInt(serviceId)));
      } catch {}
    }
  }, [serviceId]);
  const [bookingForm, setBookingForm] = useState({
    address: '',
    date: '',
    time: '',
    phone: '',
    notes: ''
  });
  const [formErrors, setFormErrors] = useState({});

  // 收藏切换函数
  const toggleFavorite = () => {
    const serviceIdNum = parseInt(serviceId);
    const newFavorites = isFavorited
      ? favorites.filter(id => id !== serviceIdNum)
      : [...favorites, serviceIdNum];

    setFavorites(newFavorites);
    setIsFavorited(!isFavorited);
    localStorage.setItem('housekeeping_favorites', JSON.stringify(newFavorites));
  };

  if (!service) {
    return (
      <div className="service-detail-page">
        <header className="detail-header">
          <Link to="/" className="back-btn">← 返回</Link>
          <h1>服务详情</h1>
        </header>
        <div className="container">
          <div className="not-found">未找到对应的服务</div>
        </div>
      </div>
    );
  }

  // 获取当前日期和时间（用于限制选择）
  const getCurrentDateTime = () => {
    const now = new Date();
    // 增加4小时的限制
    const minDateTime = new Date(now.getTime() + 4 * 60 * 60 * 1000);
    
    const year = minDateTime.getFullYear();
    const month = String(minDateTime.getMonth() + 1).padStart(2, '0');
    const day = String(minDateTime.getDate()).padStart(2, '0');
    const hours = String(minDateTime.getHours()).padStart(2, '0');
    const minutes = String(minDateTime.getMinutes()).padStart(2, '0');
    
    return {
      minDate: `${year}-${month}-${day}`,
      minTime: `${hours}:${minutes}`,
      minDateTime: minDateTime,
      today: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`
    };
  };

  const { minDate, minTime, minDateTime, today } = getCurrentDateTime();

  // 检查选定日期是否是今天
  const isToday = (selectedDate) => {
    return selectedDate === today;
  };

  // 获取对应日期的最小时间
  const getMinTimeForDate = (selectedDate) => {
    if (isToday(selectedDate)) {
      return minTime;
    }
    return '00:00';
  };

  // 验证手机号码格式
  const validatePhone = (phone) => {
    const phoneRegex = /^1[3-9]\d{9}$/;
    return phoneRegex.test(phone);
  };

  // 验证表单
  const validateForm = () => {
    const errors = {};
    
    if (!bookingForm.address.trim()) {
      errors.address = '请填写服务地址';
    }
    
    if (!bookingForm.date) {
      errors.date = '请选择服务日期';
    }
    
    if (!bookingForm.time) {
      errors.time = '请选择服务时间';
    }
    
    if (!bookingForm.phone.trim()) {
      errors.phone = '请填写联系电话';
    } else if (!validatePhone(bookingForm.phone)) {
      errors.phone = '请填写正确的手机号码';
    }

    // 验证选择的时间不能早于当前时间+4小时
    if (bookingForm.date && bookingForm.time) {
      const selectedDateTime = new Date(`${bookingForm.date} ${bookingForm.time}`);
      const now = new Date();
      const fourHoursLater = new Date(now.getTime() + 4 * 60 * 60 * 1000);
      
      if (selectedDateTime < fourHoursLater) {
        const formatTime = fourHoursLater.toLocaleTimeString('zh-CN', { 
          hour: '2-digit', 
          minute: '2-digit',
          hour12: false 
        });
        const formatDate = fourHoursLater.toLocaleDateString('zh-CN');
        errors.time = `服务时间必须在 ${formatDate} ${formatTime} 之后`;
      }
    }
    
    return errors;
  };

  // 处理表单输入
  const handleInputChange = (field, value) => {
    setBookingForm(prev => {
      const newForm = {
        ...prev,
        [field]: value
      };
      
      // 如果选择的是日期，并且是今天，需要检查时间是否有效
      if (field === 'date' && isToday(value) && prev.time) {
        const selectedDateTime = new Date(`${value} ${prev.time}`);
        const now = new Date();
        const fourHoursLater = new Date(now.getTime() + 4 * 60 * 60 * 1000);
        
        // 如果当前选择的时间无效，清空时间选择
        if (selectedDateTime < fourHoursLater) {
          newForm.time = '';
        }
      }
      
      return newForm;
    });
    
    // 清除对应字段的错误信息
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  // 点击立即预约按钮
  const handleBookNow = () => {
    setShowBookingModal(true);
  };

  // 关闭预约弹窗
  const handleCloseModal = () => {
    setShowBookingModal(false);
    setFormErrors({});
  };

  // 确认预约
  const handleConfirmBooking = () => {
    // 实时检查时间限制（防止时间过期）
    const now = new Date();
    const fourHoursLater = new Date(now.getTime() + 4 * 60 * 60 * 1000);
    const selectedDateTime = new Date(`${bookingForm.date} ${bookingForm.time}`);
    
    if (selectedDateTime < fourHoursLater) {
      alert('所选时间已过期，请重新选择至少4小时后的时间');
      return;
    }
    
    const errors = validateForm();
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    // 表单验证通过，关闭预约弹窗，跳转到支付页面
    setShowBookingModal(false);
    
    // 将预约信息作为查询参数传递给支付页面
    const bookingParams = new URLSearchParams({
      serviceId: service.id,
      address: bookingForm.address,
      date: bookingForm.date,
      time: bookingForm.time,
      phone: bookingForm.phone,
      notes: bookingForm.notes || ''
    });
    
    navigate(`/payment?${bookingParams.toString()}`);
  };

  return (
    <div className="service-detail-page">
      <header className="detail-header">
        <Link to="/" className="back-btn">← 返回</Link>
        <h1>服务详情</h1>
      </header>

      <div className="container">
        <div className="hero">
          <img src={service.image} alt={service.name} loading="lazy" />
          <div className="hero-meta">
            <div className="hero-title-row">
              <h2>{service.name}</h2>
              <button
                className={`favorite-btn-detail ${isFavorited ? 'favorited' : ''}`}
                onClick={toggleFavorite}
                title={isFavorited ? '取消收藏' : '收藏'}
              >
                {isFavorited ? '❤️' : '🤍'}
              </button>
            </div>
            <p className="price">¥{service.price} / {service.duration}</p>
          </div>
        </div>

        <section className="section">
          <h3>服务介绍</h3>
          <p className="desc">{service.description}</p>
        </section>

        {service.features?.length ? (
          <section className="section">
            <h3>服务特色</h3>
            <ul className="tag-list">
              {service.features.map((f, i) => (
                <li key={i} className="tag">{f}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {service.includes?.length ? (
          <section className="section">
            <h3>包含内容</h3>
            <ul className="bullet-list">
              {service.includes.map((f, i) => (
                <li key={i}>{f}</li>
              ))}
            </ul>
          </section>
        ) : null}

        {service.notes ? (
          <section className="section">
            <h3>服务说明</h3>
            <div className="notes">{service.notes}</div>
          </section>
        ) : null}
      </div>

      <div className="detail-footer">
        <div className="footer-info">
          <span className="footer-price">¥{service.price}</span>
          <span className="footer-duration">{service.duration}</span>
        </div>
        <button className="book-now" onClick={handleBookNow}>立即预约</button>
      </div>

      {/* 预约信息弹窗 */}
      {showBookingModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="booking-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>预约信息</h3>
              <button className="close-btn" onClick={handleCloseModal}>×</button>
            </div>
            
            <div className="modal-body">
              <div className="form-group">
                <label>服务地址 *</label>
                <textarea
                  placeholder="请填写详细的服务地址"
                  value={bookingForm.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                  className={formErrors.address ? 'error' : ''}
                />
                {formErrors.address && <span className="error-text">{formErrors.address}</span>}
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label>服务日期 *</label>
                  <input
                    type="date"
                    min={minDate}
                    value={bookingForm.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className={formErrors.date ? 'error' : ''}
                  />
                  {formErrors.date && <span className="error-text">{formErrors.date}</span>}
                </div>
                
                <div className="form-group">
                  <label>服务时间 *</label>
                  <input
                    type="time"
                    min={getMinTimeForDate(bookingForm.date)}
                    value={bookingForm.time}
                    onChange={(e) => handleInputChange('time', e.target.value)}
                    className={formErrors.time ? 'error' : ''}
                    disabled={!bookingForm.date}
                  />
                  {formErrors.time && <span className="error-text">{formErrors.time}</span>}
                  <small className="time-hint">
                    {isToday(bookingForm.date) 
                      ? `今天最早可选时间：${minTime}（当前时间+4小时）`
                      : '请选择服务时间'
                    }
                  </small>
                </div>
              </div>
              
              <div className="form-group">
                <label>联系电话 *</label>
                <input
                  type="tel"
                  placeholder="请填写11位手机号码"
                  value={bookingForm.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  className={formErrors.phone ? 'error' : ''}
                />
                {formErrors.phone && <span className="error-text">{formErrors.phone}</span>}
              </div>
              
              <div className="form-group">
                <label>注意事项</label>
                <textarea
                  placeholder="请填写需要特别注意的事项（可选）"
                  value={bookingForm.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                />
              </div>
            </div>
            
            <div className="modal-footer">
              <button className="cancel-btn" onClick={handleCloseModal}>取消</button>
              <button className="confirm-btn" onClick={handleConfirmBooking}>确认预约</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceDetailPage;

